/**
 * Dashboard-Startseite (Phase 1): Multi-Server-Übersicht als anpassbares
 * Panel-Grid (Drag/Resize/Hide, persistent). Eine Kachel pro Server mit
 * Live-Status/Spieler/Map/Modus/Tickets — initial über REST (server_status),
 * live über das WS-Multi-Abo. Klick auf eine Kachel öffnet Players.
 */

import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { useChangedPulse } from "../use-changed-pulse";
import { usePolling, useDashboardClock } from "../use-polling";
import { api, servers, overview, serverSignalAt, dashboardSparkHistory, appendDashboardSparkHistory, setActiveServer, subscribeServers, unsubscribeServers, loadOverview, type ServerChoice } from "../store";
import { t } from "../i18n";
import type { ApiResponse } from "../api";
import { navigate } from "../router";
import { PageHead } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { formatBattleResource, formatMapMode, formatMapName, modeLabel } from "../parse";
import {
  cacheAgeLabel,
  cacheAgeState,
  cpuLoadGraphPercent,
  fallbackDashboardServers,
  formatAge,
  formatBytes,
  formatCpuLoadSubtext,
  meterLevel,
  qpmBarPercent,
  RCON_QPM_PENDING,
  rconHealthLabel,
  rconHealthState,
  rconHealthTone,
  resolveSignalAtMs,
  serverRconQpmLabel,
  signalAgeTone,
  summarizeDashboardMatrix,
  type DashboardSparkHistory,
} from "../dashboard-health";
import { InteractiveSparkline, sparklineAxisLabels, type SparklineAxisLabels, type SparklineTone } from "../metric-sparkline";
import { indexAgentSummaries, type AgentCardStatus, type AgentServerStatusSummary } from "../agent-card-stats";
import { formatPercent, formatUptime, formatClock, DASH } from "../agent-live";
import type { TranslationKey } from "../i18n";

interface HostMetrics {
  label: string;
  cpu: {
    label: string;
    percent?: number;
    load1?: number;
    load5?: number;
    load15?: number;
    cores?: number;
  };
  memory: {
    totalBytes: number;
    usedBytes: number;
    freeBytes: number;
    percent: number;
  };
  disk?: {
    path: string;
    totalBytes: number;
    usedBytes: number;
    freeBytes: number;
    percent: number;
  };
}

interface DashboardServerMetric {
  id: string;
  name: string;
  online: boolean;
  map?: string;
  mode?: string;
  players?: number;
  maxPlayers?: number;
  ageSeconds?: number;
  rconQpm?: number | null;
  rconErrorsPerMinute?: number;
  lastRconError?: string | null;
  lastRconOkAge?: number;
}

interface DashboardMetrics {
  host: HostMetrics;
  servers: DashboardServerMetric[];
  rcon: {
    qpmTotal?: number | null;
    errorsPerMinute?: number;
    lastError?: string | null;
    note: string;
  };
}

function percent(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, n));
}

function MetricBar({ value }: { value: number | null }) {
  const p = value === null ? 0 : value;
  return <div class={`health-meter ${meterLevel(value)}`}><i style={`width:${p}%`} /></div>;
}

function HealthCard({
  label,
  value,
  sub,
  pct,
  sparkValues,
  sparkTone,
  sparkScaleMax,
  sparkAxis,
}: {
  label: string;
  value: string;
  sub?: string;
  pct?: number | null;
  sparkValues: number[];
  sparkTone: SparklineTone;
  sparkScaleMax?: number;
  sparkAxis?: SparklineAxisLabels;
}) {
  const changed = useChangedPulse(value);
  return (
    <div class="health-card">
      <div class="health-card-main">
        <div class="label">{label}</div>
        <div class={`health-value mono ${changed ? "value-changed" : ""}`}>{value}</div>
        {sub && <div class="health-sub">{sub}</div>}
        {pct !== undefined && <MetricBar value={pct} />}
      </div>
      <InteractiveSparkline values={sparkValues} tone={sparkTone} label={`${label} session graph`} scaleMax={sparkScaleMax} axis={sparkAxis ?? sparklineAxisLabels(sparkTone)} />
    </div>
  );
}

/** Span, der bei Änderung von `watch` kurz aufleuchtet (Dashboard-Kennzahl). */
function PulseStat({ watch, class: cls, children }: { watch: unknown; class?: string; children: ComponentChildren }) {
  const changed = useChangedPulse(watch);
  return <span class={`${cls ?? ""} ${changed ? "value-changed" : ""}`}>{children}</span>;
}

function HealthPanel({ metrics, loaded, history }: { metrics: DashboardMetrics | null; loaded: boolean; history: DashboardSparkHistory }) {
  if (!metrics) {
    if (loaded) {
      return (
        <div class="panel health-panel">
          <div class="panel-body">
            <div class="health-head">
              <div>
                <div class="eyebrow">CONTROL HOST</div>
                <h2>{t("dashboard.hostTelemetry")}</h2>
              </div>
              <span class="badge">dashboard_metrics unavailable</span>
            </div>
            <div class="health-grid">
              <HealthCard label={t("dashboard.cpuLoad")} value="—" sub="unavailable" sparkValues={history.cpuLoadPct} sparkTone="cpu" sparkScaleMax={100} />
              <HealthCard label={t("dashboard.ram")} value="—" sub="unavailable" pct={null} sparkValues={history.ramPct} sparkTone="ram" sparkScaleMax={100} />
              <HealthCard label={t("dashboard.disk")} value="—" sub="unavailable" pct={null} sparkValues={history.diskPct} sparkTone="disk" sparkScaleMax={100} />
              <HealthCard label={t("dashboard.rconQpm")} value="—" sub={RCON_QPM_PENDING} sparkValues={history.rconQpm} sparkTone="rcon" />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div class="panel health-panel">
        <div class="panel-body empty">{t("dashboard.loadingHost")}</div>
      </div>
    );
  }
  const memPct = percent(metrics.host.memory.percent);
  const diskPct = metrics.host.disk ? percent(metrics.host.disk.percent) : null;
  const cpu = metrics.host.cpu;
  const load1 = Number(cpu.load1 ?? 0);
  const load5 = Number(cpu.load5 ?? 0);
  const load15 = Number(cpu.load15 ?? 0);
  return (
    <div class="panel health-panel">
      <div class="panel-body">
        <div class="health-head">
          <div>
            <div class="eyebrow">CONTROL HOST</div>
            <h2>{t("dashboard.hostTelemetry")}</h2>
          </div>
          <span class="badge">{t("dashboard.gameHostNotConnected")}</span>
        </div>
        <div class="health-grid">
          <HealthCard
            label={t("dashboard.cpuLoad")}
            value={`${load1.toFixed(2)} / ${load5.toFixed(2)} / ${load15.toFixed(2)}`}
            sub={formatCpuLoadSubtext(cpu.cores)}
            sparkValues={history.cpuLoadPct}
            sparkTone="cpu"
            sparkScaleMax={100}
          />
          <HealthCard
            label={t("dashboard.ram")}
            value={`${Math.round(memPct ?? 0)}%`}
            sub={`${formatBytes(metrics.host.memory.usedBytes)} / ${formatBytes(metrics.host.memory.totalBytes)}`}
            pct={memPct}
            sparkValues={history.ramPct}
            sparkTone="ram"
            sparkScaleMax={100}
          />
          <HealthCard
            label={t("dashboard.disk")}
            value={metrics.host.disk ? `${Math.round(diskPct ?? 0)}%` : "—"}
            sub={metrics.host.disk ? `${metrics.host.disk.path} · ${formatBytes(metrics.host.disk.usedBytes)} / ${formatBytes(metrics.host.disk.totalBytes)}` : "unavailable"}
            pct={metrics.host.disk ? diskPct : null}
            sparkValues={history.diskPct}
            sparkTone="disk"
            sparkScaleMax={100}
          />
          <HealthCard
            label={t("dashboard.rconQpm")}
            value={serverRconQpmLabel(metrics.rcon.qpmTotal)}
            sub={metrics.rcon.errorsPerMinute ? `RCON health degraded · ${metrics.rcon.lastError ?? "last error recorded"}` : (metrics.rcon.note || RCON_QPM_PENDING)}
            sparkValues={history.rconQpm}
            sparkTone="rcon"
          />
        </div>
      </div>
    </div>
  );
}

function ServerMatrix({ rows, metricsAt }: { rows: DashboardServerMetric[]; metricsAt: number | null }) {
  const summary = summarizeDashboardMatrix(rows);
  const maxQpm = rows.reduce((max, row) => Math.max(max, Number(row.rconQpm ?? 0) || 0), 0);
  // Ein 1s-UI-Ticker (zentral hier, NICHT pro Zeile → genau ein Timer) lässt
  // "Last Signal" live hochzählen, ohne jede Sekunde zu fetchen.
  const now = useDashboardClock(1000);
  // Echte per-Server Signalzeiten aus dem WS-Store (vom Worker-Push gesetzt).
  const signals = serverSignalAt.value;
  return (
    <div class="panel server-matrix-panel">
      <div class="panel-body">
        <div class="health-head">
          <div>
            <div class="eyebrow">SERVER STATE</div>
            <h2>{t("dashboard.liveCacheMatrix")}</h2>
          </div>
          <span class="badge">RCON QPM last 60s</span>
        </div>
        <div class="matrix-summary mono">
          <PulseStat watch={summary.total}>{summary.total} servers</PulseStat>
          <PulseStat watch={summary.live}>{summary.live} live</PulseStat>
          <PulseStat watch={summary.offline}>{summary.offline} offline</PulseStat>
          <PulseStat watch={summary.qpm}>{summary.qpm} qpm</PulseStat>
          <PulseStat watch={summary.down} class={summary.down > 0 ? "warn-text" : ""}>{summary.down > 0 ? `${summary.down} down` : "rcon health ok"}</PulseStat>
          {summary.degraded > 0 && <span class="warn-text">{summary.degraded} degraded</span>}
        </div>
        <div class="table-wrap">
          <table class="scoreboard server-matrix stable-matrix">
            <thead>
              <tr>
                <th>{t("dashboard.colServer")}</th>
                <th>{t("dashboard.colState")}</th>
                <th>{t("dashboard.colMapMode")}</th>
                <th class="num">{t("dashboard.colPlayers")}</th>
                <th class="num">{t("dashboard.colCacheAge")}</th>
                <th class="num">{t("dashboard.colRconQpm")}</th>
                <th>{t("dashboard.colRconHealth")}</th>
                <th class="num">{t("dashboard.colLastSignal")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} class="empty matrix-empty">{t("dashboard.noServers")}</td></tr>
              ) : rows.map((s) => <MatrixRow key={s.id} s={s} maxQpm={maxQpm} now={now} metricsAt={metricsAt} signalAt={signals[s.id]} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Eine Matrix-Zeile. Eigene Komponente, damit `useChangedPulse` pro Server
 * stabil ist (Hook nicht im map-Callback) — der RCON-QPM-Wert flasht bei echter
 * Änderung durch ein Live-Update. */
function MatrixRow({ s, maxQpm, now, metricsAt, signalAt }: { s: DashboardServerMetric; maxQpm: number; now: number; metricsAt: number | null; signalAt: number | undefined }) {
  const ageState = cacheAgeState(s.online, s.ageSeconds);
  const health = rconHealthState(s);
  const qpmChanged = useChangedPulse(s.rconQpm);
  // Live-Aging von "Last Signal" PRO SERVER: Vorrang hat die echte per-Server
  // WS-Push-Zeit (`signalAt`) — die altert unabhängig und springt nur bei einem
  // echten Signal für genau diesen Server auf 0s. Solange (noch) kein Push kam,
  // wird der Zeitpunkt aus dem server-individuellen Poll-Age rekonstruiert
  // (kein pauschales Gleichstempeln aller Server durch den Poll).
  const baseAgeSec = s.lastRconOkAge ?? s.ageSeconds;
  const signalAtMs = resolveSignalAtMs(signalAt, metricsAt, baseAgeSec);
  const liveAgeSec = signalAtMs !== null ? Math.max(0, Math.floor((now - signalAtMs) / 1000)) : baseAgeSec;
  const signalTone = signalAgeTone(liveAgeSec);
  return (
    <tr class={`matrix-row ${ageState}`}>
      <td data-label="Server">
        <strong>{s.name}</strong>
        <div class="cell-sub mono">{s.id}</div>
      </td>
      <td data-label="State">
        <span class={`matrix-state ${ageState}`}>
          <span class={`live-dot ${s.online ? "open" : ""}`} />
          {s.online ? t("common.online") : t("common.offline")}
        </span>
      </td>
      <td data-label="Map / Mode">
        <span class="matrix-map">{formatMapMode(s.map, s.mode)}</span>
      </td>
      <td class="num mono" data-label="Players">{s.players !== undefined ? `${s.players}/${s.maxPlayers || "–"}` : "—"}</td>
      <td class="num mono" data-label="Cache Age">
        <span class={`age-pill ${ageState}`}>{cacheAgeLabel(s.online, s.ageSeconds)}</span>
        <div class="cell-sub">{formatAge(s.ageSeconds)}</div>
      </td>
      <td class="num mono telemetry-qpm" data-label={t("dashboard.rconQpm")}>
        <div class={`qpm-value ${qpmChanged ? "value-changed" : ""}`}>{serverRconQpmLabel(s.rconQpm)}</div>
        <div class="qpm-mini"><i style={`width:${qpmBarPercent(s.rconQpm, maxQpm)}%`} /></div>
      </td>
      <td class="mono" data-label="RCON Health" title={s.lastRconError ?? undefined}>
        <span class={`rcon-health ${rconHealthTone(health)}`}>{rconHealthLabel(health)}</span>
        {s.lastRconError ? <div class="cell-sub danger-text">last error in telemetry</div> : null}
      </td>
      <td class="num mono" data-label="Last Signal">
        <span class={`last-signal-age ${signalTone}`}>
          {liveAgeSec !== undefined ? `${formatAge(liveAgeSec)} ago` : formatAge(s.ageSeconds)}
        </span>
      </td>
    </tr>
  );
}

const AGENT_STATUS_LABEL: Record<AgentCardStatus, TranslationKey> = {
  online: "agentLive.statusOnline",
  stale: "agentLive.statusStale",
  offline: "agentLive.statusOffline",
  unknown: "agentLive.statusUnknown",
};

/**
 * Kompakte Agent-/Host-Kurzanzeige auf einer Server-Card. Ohne Agent-Daten
 * dezent „Agent —“ (nicht laut rot). CPU/RAM/Disk/Queue null-safe über die
 * wiederverwendeten Formatter aus `agent-live.ts`.
 */
function AgentCardStats({ agent }: { agent: AgentServerStatusSummary | undefined }) {
  if (!agent) {
    return (
      <div class="tile-agent tile-agent--none" title={t("dashboard.agentNone")}>
        <span class="tile-agent-label">{t("dashboard.agent")}</span>
        <span class="tile-agent-dash">{DASH}</span>
      </div>
    );
  }
  return (
    <div class={`tile-agent tile-agent--${agent.status}`}>
      <span class="tile-agent-head">
        <span class="tile-agent-label">{t("dashboard.agent")}</span>
        <span class={`tile-agent-status tile-agent-status--${agent.status}`}>
          <span class="live-dot" />
          {t(AGENT_STATUS_LABEL[agent.status])}
        </span>
        <span class="tile-agent-hb mono" title={t("agentLive.lastHeartbeat")}>{formatClock(agent.last_heartbeat)}</span>
      </span>
      <span class="tile-agent-metrics mono">
        <span title={t("agentLive.cpu")}>{t("agentLive.cpu")} {formatPercent(agent.cpu_percent)}</span>
        <span class="dot-sep">·</span>
        <span title={t("agentLive.ram")}>{t("agentLive.ram")} {formatPercent(agent.memory_percent)}</span>
        <span class="dot-sep">·</span>
        <span title={t("agentLive.disk")}>{t("agentLive.disk")} {formatPercent(agent.disk_percent)}</span>
        <span class="dot-sep">·</span>
        <span title={t("agentLive.queue")}>{t("agentLive.queue")} {agent.queue ?? DASH}</span>
        {agent.uptime_seconds != null && (
          <>
            <span class="dot-sep">·</span>
            <span title={t("agentLive.uptime")}>{formatUptime(agent.uptime_seconds)}</span>
          </>
        )}
      </span>
    </div>
  );
}

function ServerTile({ s, agent }: { s: ServerChoice; agent: AgentServerStatusSummary | undefined }) {
  const o = overview.value[s.id];
  const now = Math.floor(Date.now() / 1000);
  const live = !!o && now - o.ts < 20;
  const resource = formatBattleResource(o?.mode ?? "", o?.t1 ?? 0, o?.t2 ?? 0);
  return (
    <div class="panel tile" onClick={() => { setActiveServer(s.id); navigate("players"); }}>
      <div class="panel-body">
        <div class="row" style="justify-content:space-between;margin-bottom:var(--s4)">
          <h2 style="text-transform:none;letter-spacing:0;font-size:15px">{s.name}</h2>
          <span class="badge"><span class={`live-dot ${live ? "open" : ""}`} />{live ? "Live" : "Offline"}</span>
        </div>
        <div class="tile-grid">
          <div><div class="label">Spieler</div><div class="tile-num mono">{o ? `${o.players}/${o.maxPlayers || "–"}` : "—"}</div></div>
          <div><div class="label">{resource.label}</div><div class="tile-num mono" style={resource.titan ? "font-size:13px;line-height:1.35" : undefined}><span class="dot pac" />{o ? resource.pac : "—"}<span class="faint" style="margin:0 4px">{resource.titan ? "/" : ":"}</span><span class="dot eu" />{o ? resource.eu : "—"}</div></div>
          <div><div class="label">Map</div><div class="tile-v">{o?.map ? formatMapName(o.map) : "—"}</div></div>
          <div><div class="label">Modus</div><div class="tile-v">{modeLabel(o?.mode || "")}</div></div>
        </div>
        <AgentCardStats agent={agent} />
      </div>
    </div>
  );
}

/** Live-Poll-Intervall fürs Dashboard (dashboard_metrics deckt Host-Telemetrie
 * UND die Live-Cache-Matrix/RCON-QPM/Last-Signal in EINEM Call ab → 5s trifft
 * beide Vorgaben (Matrix 3–5s, Host 5–10s), ohne nervös zu sein. */
export const DASHBOARD_POLL_MS = 5000;

export function Dashboard() {
  const edit = useSignal(false);
  const metrics = useSignal<DashboardMetrics | null>(null);
  const metricsLoaded = useSignal(false);
  const lastMetricsAt = useSignal<number | null>(null);
  // Per-Server Agent-/Host-Kurzstatus für die Server-Cards (read-only Action,
  // best-effort: ohne Permission/Heartbeats bleibt die Map leer → Cards zeigen
  // dezent „Agent —“). server_id → Summary.
  const agentStatus = useSignal<Record<string, AgentServerStatusSummary>>({});
  const list = servers.value;
  const ids = list.map((s) => s.id);
  const key = ids.join("|");

  async function loadAgentStatus(): Promise<void> {
    try {
      const r = await api.call<ApiResponse & { servers?: unknown }>("agent_server_status_summary");
      agentStatus.value = r.ok ? indexAgentSummaries(r.servers) : {};
    } catch {
      // Fehler/kein Zugriff → keine Agent-Kurzanzeige, aber Dashboard bleibt heil.
      agentStatus.value = {};
    }
  }

  async function loadMetrics(): Promise<void> {
    try {
      const r = await api.call<ApiResponse & DashboardMetrics>("dashboard_metrics");
      if (r.ok) {
        metrics.value = { host: r.host, servers: r.servers, rcon: r.rcon };
        // Baseline für das Live-Aging von "Last Signal" NUR bei Erfolg setzen:
        // ein fehlgeschlagener Poll darf die relative Zeit nicht zurücksetzen.
        lastMetricsAt.value = Date.now();
        appendDashboardSparkHistory({
          cpuLoadPct: cpuLoadGraphPercent(r.host.cpu.load1, r.host.cpu.cores),
          ramPct: r.host.memory.percent,
          diskPct: r.host.disk?.percent,
          rconQpm: r.rcon.qpmTotal ?? undefined,
        });
      } else metrics.value = null;
    } catch {
      metrics.value = null;
    } finally {
      metricsLoaded.value = true;
    }
  }

  async function refresh(): Promise<void> {
    await Promise.all([loadOverview(ids), loadMetrics(), loadAgentStatus()]);
  }

  useEffect(() => {
    void loadOverview(ids);
    void loadMetrics();
    void loadAgentStatus();
    subscribeServers(ids);
    return () => unsubscribeServers(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Live-Auto-Update: pollt dashboard_metrics alle DASHBOARD_POLL_MS. Kein
  // Doppel-Load beim Mount (Poller feuert erst nach dem Intervall), kein Overlap,
  // Cleanup beim Unmount, im versteckten Tab langsamer. WS hält Spielerzahlen/
  // Tickets ohnehin live; das hier deckt Host-Telemetrie + RCON-Health/QPM ab.
  usePolling(loadMetrics, DASHBOARD_POLL_MS);
  // Agent-Kurzstatus im gleichen Takt nachziehen (eigener Poller → unabhängig,
  // kein Overlap; Heartbeats kommen ~alle 30 s, 5 s ist reichlich frisch).
  usePolling(loadAgentStatus, DASHBOARD_POLL_MS);

  const agents = agentStatus.value;
  const items: PanelItem[] = list.map((s) => ({
    id: `srv:${s.id}`,
    title: s.name,
    defaultSpan: 4,
    render: () => <ServerTile s={s} agent={agents[s.id]} />,
  }));
  const metricRows = metrics.value?.servers ?? [];
  const matrixRows = metricRows.length > 0 || list.length === 0
    ? metricRows
    : fallbackDashboardServers(list, overview.value);

  return (
    <div class="view-enter">
      <PageHead title={t("dashboard.title")} sub={`${list.length} ${t("dashboard.serversInScope")}`}>
        <span class="dash-auto mono" title={lastMetricsAt.value ? `${t("common.lastSignal")}: ${new Date(lastMetricsAt.value).toLocaleTimeString()}` : t("common.auto")}>
          <span class="live-dot open" />{t("common.auto")} · {Math.round(DASHBOARD_POLL_MS / 1000)}s
        </span>
        <button class="btn sm" onClick={() => void refresh()}>↻ {t("common.refresh")}</button>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>
          {edit.value ? t("dashboard.editDone") : t("dashboard.edit")}
        </button>
      </PageHead>
      <HealthPanel metrics={metrics.value} loaded={metricsLoaded.value} history={dashboardSparkHistory.value} />
      <ServerMatrix rows={matrixRows} metricsAt={lastMetricsAt.value} />
      {list.length === 0 ? (
        <div class="panel"><div class="panel-body empty">{t("dashboard.noServers")}</div></div>
      ) : (
        <PanelGrid view="dashboard" items={items} edit={edit} />
      )}
    </div>
  );
}
